import ApplyConversationActionContext from 'owa-mail-store/lib/store/schema/ApplyConversationActionContext';
import ApplyConversationActionRequest from 'owa-service/lib/contract/ApplyConversationActionRequest';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ConversationAction from 'owa-service/lib/contract/ConversationAction';
import applyConversationActionRequest from 'owa-service/lib/factory/applyConversationActionRequest';
import conversationAction from 'owa-service/lib/factory/conversationAction';
import folderIdFactory from 'owa-service/lib/factory/folderId';
import itemId from 'owa-service/lib/factory/itemId';
import targetFolderIdFactory from 'owa-service/lib/factory/targetFolderId';
import applyConversationActionService from 'owa-mail-store/lib/services/applyConversationActionService';

/**
 * Issue the service request to update categories of specified conversations
 * @param conversationActionContexts the conversation context to be categorized
 * @param contextFolderId the context folder id that the conversation belongs to
 * @param categoriesToAdd the categories to be added to the conversations
 * @param categoriesToRemove the categories to be removed from the conversations
 * @param clearCategories whether to clear categories
 * @return a promise which contains ApplyConversationActionResponseMessage
 */
export default function updateConversationsCategoriesService(
    conversationActionContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean
): Promise<ApplyConversationActionResponseMessage> {
    const requestBody = configureRequestBody(
        conversationActionContexts,
        contextFolderId,
        categoriesToAdd,
        categoriesToRemove,
        clearCategories
    );

    return applyConversationActionService(requestBody);
}

/**
 * Configure the ApplyConversationActionRequest
 * @param conversationActionContexts the conversation context to be categorized
 * @param contextFolderId the context folder id that the conversation belongs to
 * @param categoriesToAdd the categories to add to the conversations
 * @param categoriesToRemove the categories to be removed from the conversations
 * @param clearCategories whether to clear categories
 * @return an ApplyConversationActionRequest
 */
function configureRequestBody(
    conversationActionContexts: ApplyConversationActionContext[],
    contextFolderId: string,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean
): ApplyConversationActionRequest {
    const conversationActions = conversationActionContexts.map(conversationContext =>
        createCategorizeConversationAction(
            conversationContext,
            contextFolderId,
            categoriesToAdd,
            categoriesToRemove,
            clearCategories
        )
    );
    return applyConversationActionRequest({
        ConversationActions: conversationActions,
        ReturnMovedItemIds: false,
    });
}

/**
 * Configure the ApplyConversationActionRequest
 * @param conversationContext the conversation context to be categorized
 * @param contextFolderId the context folder id that the conversation belongs to
 * @param categoriesToAdd the categories to be added to the conversations
 * @param categoriesToRemove the categories to be removed to the conversations
 * @param clearCategories the categories to be removed to the conversations
 * @return the generated ConversationAction
 */
function createCategorizeConversationAction(
    conversationContext: ApplyConversationActionContext,
    contextFolderId: string,
    categoriesToAdd: string[],
    categoriesToRemove: string[],
    clearCategories: boolean
): ConversationAction {
    const categorizeAction = conversationAction({
        Action: 'UpdateAlwaysCategorizeRule',
        ConversationId: itemId({ Id: conversationContext.conversationId }),
        Categories: categoriesToAdd,
        CategoriesToRemove: categoriesToRemove,
        ClearCategories: clearCategories,
    });

    // folderId can be null in search mode.
    if (contextFolderId) {
        const targetFolderId = targetFolderIdFactory({
            BaseFolderId: folderIdFactory({
                Id: contextFolderId,
            }),
        });

        categorizeAction.ContextFolderId = targetFolderId;
    }
    return categorizeAction;
}
